import { IProgression } from '../../../models/course/ICourseData';
import styles from './course-progression.module.scss';
import InfoPopup from '../../../components/atoms/info-popup/info-popup';
import Markdown from 'markdown-to-jsx';
import React, { act, CSSProperties, useCallback, useRef, useState } from 'react';
import { getHeapAttributes } from '../../../utils/heap/heap.helper';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';
import useWindowResize from '../../../hooks/use-window-resize.hook';

type Props = {
  countryCode: string;
  progression: IProgression;
  programRoute: String;
};

const customStyle: CSSProperties = { bottom: '-30px', top: 'unset' }; // used for popup when it's in the bottom of the page

export default function CourseProgression({ countryCode, progression, programRoute }: Props) {
  const [activeLink, setActiveLink] = useState<string | null>(null);
  const [popupHeading, setPopupHeading] = useState<string | null>(null);
  const [popupContent, setPopupContent] = useState<string | null>(null);
  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);
  const elementRef = useRef(null);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.Medium), 100);

  const isBottomOfPage = useCallback(
    () => window.innerHeight - elementRef.current.getBoundingClientRect().bottom < 350,
    [],
  );

  const onClick = useCallback(
    (content: string, heading: string, linkType: string) =>
      (e: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => {
        e.preventDefault();

        if (activeLink === linkType) {
          closePopup();
        } else {
          setTimeout(() => {
            setPopupHeading(heading);
            setPopupContent(content);
            setActiveLink(linkType);
          }, 160);
        }
      },
    [activeLink],
  );

  const closePopup = () => {
    setPopupContent(null);
    setActiveLink(null);
    setPopupHeading(null);
  };

  return (
    <>
      <p className={styles[`row-border`]}></p>
      <div className='row position-relative' ref={elementRef}>
        <div className='col-md-12'>
          <div className='row'>
            <div className='col-md-6'>
              <h4 className={`${styles[`header`]} mt-md-3 mt-0`}>{progression.programName}</h4>
              <h5 className={`${styles[`sub-header`]} font-regular mt-md-3 mt-0`}>
                {progression.progressionRequirements && countryCode != 'US' && <>Final level: </>}{' '}
                {progression.progressionRequirements}
              </h5>
              {programRoute !== 'ICAS' &&  (
                <span className={styles[`position-right`]}>
                  {progression.offersPlacement && (
                    <p className={`${styles[`progression-legend`]} ${styles[`bg-blue`]}`}>P</p>
                  )}
                  {progression.offersYearAbroad && (
                    <p className={`${styles[`progression-legend`]} ${styles[`bg-green`]}`}>A</p>
                  )}
                </span>
              )}
              {progression.languageRequirements && (
                <h5 className={`${styles[`sub-header`]}  mt-md-3 mt-0`}>
                  English level: {progression.languageRequirements}
                </h5>
              )}
            </div>
          </div>
        </div>
        <div className='col-md-12'>
          {progression.prerequisites && (
            <div className='mb-2'>
              <span
                tabIndex={0}
                onClick={onClick(
                  progression.prerequisites,
                  'Prerequisites',
                  `${progression.productProgressionId}-prerequisites`,
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    onClick(
                      progression.prerequisites,
                      'Prerequisites',
                      `${progression.productProgressionId}-prerequisites`,
                    )(e);
                  }
                }}
                className={`${styles[`header-link`]} mt-md-3 mt-0 ${activeLink === `${progression.productProgressionId}-prerequisites` ? styles.active : null}`}
                {...getHeapAttributes(`course-prerequisites`, {
                  programName: progression.programName,
                  institutionName: progression.institutionName,
                })}
              >
                Prerequisites
              </span>
              {popupContent &&
                activeLink === `${progression.productProgressionId}-prerequisites` && (
                  <InfoPopup
                    onClose={closePopup}
                    customStyle={isBottomOfPage() && isBreakpointUp ? customStyle : null}
                    cssClass={`${styles[`progression-info`]}`}
                    heading={popupHeading}
                    hideOnTab
                  >
                    <Markdown>{popupContent}</Markdown>
                  </InfoPopup>
                )}
            </div>
          )}
          {progression.otherRequirements && (
            <div className='mt-2'>
              <span
                tabIndex={0}
                onClick={onClick(
                  progression.otherRequirements,
                  'Additional Requirements',
                  `${progression.productProgressionId}-otherRequirements`,
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    onClick(
                      progression.otherRequirements,
                      'Additional Requirements',
                      `${progression.productProgressionId}-otherRequirements`,
                    )(e);
                  }
                }}
                className={`${styles[`header-link`]} mt-md-3 mt-0 ${activeLink === `${progression.productProgressionId}-otherRequirements` ? styles.active : null}`}
                {...getHeapAttributes(`course-additional-requirements`, {
                  programName: progression.programName,
                  institutionName: progression.institutionName,
                })}
              >
                Additional Requirements
              </span>
              {popupContent &&
                activeLink === `${progression.productProgressionId}-otherRequirements` && (
                  <InfoPopup
                    onClose={closePopup}
                    customStyle={isBottomOfPage() && isBreakpointUp ? customStyle : null}
                    cssClass={`${styles[`progression-info`]}`}
                    heading={popupHeading}
                    hideOnTab
                  >
                    <Markdown>{popupContent}</Markdown>
                  </InfoPopup>
                )}
            </div>
          )}
          {progression.additionalInformation && (
            <div className='mt-2'>
              <span
                tabIndex={0}
                onClick={onClick(
                  progression.additionalInformation,
                  'Additional Information',
                  `${progression.productProgressionId}-additionalInformation`,
                )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    onClick(
                      progression.additionalInformation,
                      'Additional Information',
                      `${progression.productProgressionId}-additionalInformation`,
                    )(e);
                  }
                }}
                className={`${styles[`header-link`]}  mt-md-3 mt-0 ${activeLink === `${progression.productProgressionId}-additionalInformation` ? styles.active : null}`}
                {...getHeapAttributes(`course-additional-information`, {
                  programName: progression.programName,
                  institutionName: progression.institutionName,
                })}
              >
                Additional Information
              </span>
              {popupContent &&
                activeLink === `${progression.productProgressionId}-additionalInformation` && (
                  <InfoPopup
                    onClose={closePopup}
                    customStyle={isBottomOfPage() && isBreakpointUp ? customStyle : null}
                    cssClass={`${styles[`progression-info`]}`}
                    heading={popupHeading}
                    hideOnTab
                  >
                    <Markdown>{popupContent}</Markdown>
                  </InfoPopup>
                )}
            </div>
          )}
        </div>
        <div className='col-md-12'>
          <div className='row'>
            <div className='col-md-6'>
              {progression.cricosCode ? (
                <h5 className={`${styles[`sub-header`]}  mt-md-3 mt-0`}>
                  CRICOS Code: {progression.cricosCode}
                </h5>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
